import { ActionBarPrimitive, useAssistantState } from '@assistant-ui/react'
import {
	FrownIcon,
	GraduationCapIcon,
	MehIcon,
	SendIcon,
	SmileIcon,
	StethoscopeIcon,
	UserIcon,
	XIcon,
} from 'lucide-react'
import { type FC, useRef, useState } from 'react'
import { visibleAnswerText } from '#app/utils/answer-restart.js'
import { extractLastDataPart } from '#app/utils/data-parts.js'
import { cn } from '#app/utils/misc.tsx'
import { isTestingMode } from '#app/utils/test-mode.client.js'

type Rating = 'negative' | 'neutral' | 'positive'
type Role = 'healthcare' | 'student' | 'non_healthcare'

const ratingOptions: Array<{
	value: Rating
	Icon: typeof FrownIcon
	label: string
}> = [
	{ value: 'negative', Icon: FrownIcon, label: 'Dåligt' },
	{ value: 'neutral', Icon: MehIcon, label: 'Ok' },
	{ value: 'positive', Icon: SmileIcon, label: 'Bra' },
]

const roleOptions: Array<{
	value: Role
	Icon: typeof StethoscopeIcon
	label: string
}> = [
	{ value: 'healthcare', Icon: StethoscopeIcon, label: 'Vårdpersonal' },
	{ value: 'student', Icon: GraduationCapIcon, label: 'Studerande' },
	{ value: 'non_healthcare', Icon: UserIcon, label: 'Ej vårdpersonal' },
]

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getTextContent(content: ReadonlyArray<any>): string {
	const textPart = content.find((p: { type: string }) => p.type === 'text')
	// Ett svar som börjat om (IM-29) bär det gamla halva svaret före markören;
	// betyget och historiken ska gälla det användaren faktiskt såg.
	return visibleAnswerText(textPart?.text ?? '')
}

function ConsentCheckbox({
	checked,
	onChange,
}: {
	checked: boolean
	onChange: (checked: boolean) => void
}) {
	return (
		<label className="mt-2 flex cursor-pointer items-start gap-2 text-[11px] leading-tight text-muted-foreground/70">
			<input
				type="checkbox"
				checked={checked}
				onChange={e => onChange(e.target.checked)}
				className="mt-0.5 shrink-0 rounded accent-green-700"
			/>
			<span>Inkludera min fråga med feedbacken</span>
		</label>
	)
}

export const MessageRating: FC = () => {
	const [selectedRating, setSelectedRating] = useState<Rating | null>(null)
	const [selectedRole, setSelectedRole] = useState<Role | null>(null)
	const [comment, setComment] = useState('')
	const [showThankYou, setShowThankYou] = useState(false)
	const [includeQuestion, setIncludeQuestion] = useState(true)

	// The rating POST is captured as a Promise here so handleRole / handleComment
	// can await the id even when the user clicks faster than the POST round-trip.
	// A plain `useState` doesn't work because the state update from the resolved
	// POST wouldn't be visible inside an in-flight handler closure — only the
	// Promise gives us a stable, awaitable reference to the id.
	const ratingIdPromiseRef = useRef<Promise<string | null> | null>(null)

	const threadMessages = useAssistantState(({ thread }) => thread.messages)
	const currentMessage = useAssistantState(({ message }) => message)

	const handleRating = async (rating: Rating) => {
		setSelectedRating(rating)

		const messageIdx = threadMessages.findIndex(
			m => m.id === currentMessage.id,
		)
		const userMessage =
			messageIdx > 0 ? threadMessages[messageIdx - 1] : null

		const question = includeQuestion && userMessage
			? getTextContent(userMessage.content)
			: null
		const answer = includeQuestion
			? getTextContent(currentMessage.content)
			: null

		const assistantIndex =
			threadMessages
				.slice(0, messageIdx + 1)
				.filter(m => m.role === 'assistant').length - 1

		// Förseglad svarsreferens från servern (motor/modell/deploy). Krypterad:
		// klienten kan varken läsa innehållet eller hitta på ett eget (servern
		// avvisar allt som inte är äkta). SISTA dataparten gäller: egen motor
		// skickar en tidig och en slutlig. Skickas oavsett samtycket ovan: rutan
		// gäller frågetexten, referensen bär ingen användartext.
		const answerRef = extractLastDataPart<string>(
			currentMessage.metadata as Record<string, unknown> | undefined,
			'answerRef',
		)

		ratingIdPromiseRef.current = (async () => {
			try {
				const response = await fetch('/resources/chat-rating', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						...(isTestingMode() ? { 'x-im-testing': '1' } : {}),
					},
					body: JSON.stringify({
						rating,
						question,
						answer,
						answerRef,
						messageIndex: Math.max(0, assistantIndex),
						conversationHistory: includeQuestion
							? JSON.stringify(
									threadMessages.map(m => ({
										role: m.role,
										content: getTextContent(m.content),
									})),
								)
							: null,
					}),
				})
				const data = (await response.json()) as { id?: string }
				if (data.id) {
					return data.id
				}
			} catch {
				// Rating failed silently - don't block the user
			}
			return null
		})()
	}

	const handleRole = async (role: Role) => {
		setSelectedRole(role)
		// Wait for the in-flight POST (if any) so a fast click doesn't drop
		// the role PATCH. If no rating Promise was registered or the POST
		// returned no id, skip the PATCH (same fail-silently policy as before).
		const id = ratingIdPromiseRef.current
			? await ratingIdPromiseRef.current
			: null
		if (!id) return
		try {
			await fetch('/resources/chat-rating', {
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ id, role }),
			})
		} catch {
			// Role update failed silently
		}
	}

	const handleComment = async () => {
		if (!comment.trim()) return

		// Persistence is best-effort: if the initial rating POST failed (no id
		// on the resolved Promise) or the PATCH itself fails, the user still
		// advances to the thank-you state. Awaiting the Promise also closes
		// the same race window covered in handleRole.
		const id = ratingIdPromiseRef.current
			? await ratingIdPromiseRef.current
			: null
		if (id) {
			try {
				await fetch('/resources/chat-rating', {
					method: 'PATCH',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ id, comment: comment.trim() }),
				})
			} catch {
				// Comment failed silently
			}
		}

		setShowThankYou(true)
	}

	if (showThankYou) {
		return (
			<div className="mt-4 text-center text-sm text-muted-foreground">
				Tack för din feedback!
			</div>
		)
	}

	const showRoleStep = selectedRating && !selectedRole
	const showCommentStep = selectedRating && selectedRole

	return (
		<ActionBarPrimitive.Root hideWhenRunning autohide="not-last">
			<div className="mt-4 flex justify-center">
				<div className="relative inline-flex flex-col items-center rounded-lg border border-border/50 bg-muted/30 px-6 py-3">
					{!selectedRating && (
						<>
							<p className="mb-2 text-sm font-medium text-muted-foreground">
								Vad tyckte du om svaret?
							</p>
							<div className="flex items-center gap-4">
								{ratingOptions.map(({ value, Icon, label }) => (
									<button
										key={value}
										type="button"
										onClick={() => handleRating(value)}
										className="flex flex-col items-center gap-1 rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
										aria-label={label}
									>
										<Icon className="size-7" />
										<span className="text-[11px]">
											{label}
										</span>
									</button>
								))}
							</div>
							<ConsentCheckbox
								checked={includeQuestion}
								onChange={setIncludeQuestion}
							/>
						</>
					)}

					{showRoleStep && (
						<div className="w-full duration-200 animate-in fade-in slide-in-from-top-1">
							<button
								type="button"
								onClick={() => setShowThankYou(true)}
								className="absolute right-2 top-2 rounded p-1 text-muted-foreground/50 transition-colors hover:text-muted-foreground"
								aria-label="Stäng"
							>
								<XIcon className="size-4" />
							</button>
							<p className="mb-2 text-center text-sm font-medium text-muted-foreground">
								Vilken roll har du?{' '}
								<span className="font-normal text-muted-foreground/70">
									(valfritt)
								</span>
							</p>
							<div className="flex flex-wrap justify-center gap-2">
								{roleOptions.map(({ value, Icon, label }) => (
									<button
										key={value}
										type="button"
										onClick={() => handleRole(value)}
										className="flex min-w-[96px] flex-col items-center gap-1 rounded-lg border border-border/60 bg-background px-3 py-2 transition-colors hover:border-green-700 hover:bg-muted"
										aria-label={label}
									>
										<Icon className="size-6 text-green-700" />
										<span className="text-[11px] leading-tight text-foreground/80">
											{label}
										</span>
									</button>
								))}
							</div>
						</div>
					)}

					{showCommentStep && (
						<div className="w-full max-w-xs duration-200 animate-in fade-in slide-in-from-top-1">
							<p className="mb-2 text-center text-xs text-muted-foreground">
								Beskriv gärna ditt betyg{' '}
								<span className="text-muted-foreground/70">
									(valfritt)
								</span>
							</p>
							<div className="relative">
								<input
									type="text"
									value={comment}
									onChange={e => setComment(e.target.value)}
									onKeyDown={e => {
										if (
											e.key === 'Enter' &&
											comment.trim()
										) {
											handleComment()
										}
									}}
									placeholder="Skriv här..."
									className="h-9 w-full rounded-md border border-input bg-background px-3 pr-16 text-sm outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring"
									autoFocus
								/>
								<div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-0.5">
									<button
										type="button"
										onClick={handleComment}
										disabled={!comment.trim()}
										className={cn(
											'rounded p-1 transition-colors',
											comment.trim()
												? 'text-foreground hover:bg-muted'
												: 'text-muted-foreground/30',
										)}
										aria-label="Skicka"
									>
										<SendIcon className="size-4" />
									</button>
									<button
										type="button"
										onClick={() => setShowThankYou(true)}
										className="rounded p-1 text-muted-foreground/50 transition-colors hover:text-muted-foreground"
										aria-label="Stäng"
									>
										<XIcon className="size-4" />
									</button>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		</ActionBarPrimitive.Root>
	)
}
